import { useMutation, useQuery } from '@apollo/client'
import { useState } from 'react'

import { gql } from '@/clients/utoniq-core'
import {
  MyProfileEditQuery,
  MyProfileQueryVariables,
  UpdateMyProfileEditMutation,
  UpdateMyProfileEditMutationVariables,
  UserConnectionFragmentDoc,
} from '@/clients/utoniq-core/schema'
import { config } from '@/config'
import {
  useAuth,
  useCurrentUser,
  useMessage,
  useRouter,
  useTranslation,
  useUserTmpUploader,
} from '@/core'
import { makeViewModel } from '@/core/architecture'
import { Camera } from '@/core/native'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { user } = useCurrentUser()
  const { router, routerPages } = useRouter()
  const { reloadUser } = useAuth()
  const { t } = useTranslation()
  const { showError } = useMessage()
  const { getImage } = Camera
  const { upload } = useUserTmpUploader(user?.uid ?? '')
  const [avatarUploading, setAvatarUploading] = useState<boolean>(false)
  const { data } = useQuery<MyProfileEditQuery, MyProfileQueryVariables>(QUERY)
  const [updateUserProfile, { loading: userProfileUpdating }] = useMutation<
    UpdateMyProfileEditMutation,
    UpdateMyProfileEditMutationVariables
  >(MUTATION)

  return {
    state: {
      userProfileUpdating,
      avatarUploading,
      user: data?.user ?? null,
    },
    actions: {
      async onSubmitMyProfile(values) {
        await updateUserProfile({
          variables: {
            updateUserProfileInputs: {
              displayName: values.displayName,
              accountId: values.accountId,
              introduction: values.introduction,
              avatarPath: values.avatarPath,
            },
          },
        })
        // re-fetch user from auth
        await reloadUser()
        await router.goBack(routerPages.MyProfile)
      },
      onClickAvatar() {
        return getImage(config.upload.mimeTypes.image)
          .then(file => {
            if (file.size > config.upload.limit['image']) {
              throw new Error('uploadErrorMaximumSize')
            }
            setAvatarUploading(true)
            return upload(file)
          })
          .catch(error => {
            if (error.message === 'uploadErrorMaximumSize') {
              showError(new Error(t('uploadErrorMaximumSize')))
              return
            }
            if (error.message === 'uploadErrorImageOnly') {
              showError(new Error(t('uploadErrorImageOnly')))
              return
            }
            showError(error)
            return
          })
          .finally(() => {
            setAvatarUploading(false)
          })
      },
    },
  }
})

const QUERY = gql`
  query MyProfileEdit {
    user {
      ...UserConnection
    }
  }
  ${UserConnectionFragmentDoc}
`

const MUTATION = gql`
  mutation UpdateMyProfileEdit($updateUserProfileInputs: UpdateProfileInput!) {
    updateUserProfile(inputs: $updateUserProfileInputs) {
      ...UserConnection
    }
  }
  ${UserConnectionFragmentDoc}
`
